import type { childPartV2Param } from "@/api/breakdownService";
import { useGetMaterialCodeByFirstTierSupplier } from "@/hooks/materialSupplierHook";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import { isValidDecimalInput, parseDecimal, parseInteger } from "@/utils/inputParse";
import { Autocomplete, TableCell, TextField, Tooltip } from "@mui/material";
import { useEffect, useRef, useState } from "react";

interface ChildPartInfoDataEditProps {
  newChildPart: childPartV2Param | null;
  setNewChildPart: React.Dispatch<
    React.SetStateAction<childPartV2Param | null>
  >;
  supplierCode: string;
  validationErrors: Set<string>;
  setFlaggedReason: React.Dispatch<React.SetStateAction<Set<string>>>;
}

const ChildPartInfoDataEdit = ({
  newChildPart,
  setNewChildPart,
  supplierCode,
  validationErrors,
  setFlaggedReason,
}: ChildPartInfoDataEditProps) => {
  const {
    data: materialCodes,
    isPending: isMaterialCodesPending,
    isError: isMaterialCodesError,
  } = useGetMaterialCodeByFirstTierSupplier(supplierCode);
  const volumeRef = useRef<number>(0);
  const [ratioInput, setRatioInput] = useState<string>("");
  const [consumptionInput, setConsumptionInput] = useState<string>("");
  const [partWeightInput, setPartWeightInput] = useState<string>("");

  useEffect(() => {
    setConsumptionInput(Number(newChildPart?.consumption ?? 0).toFixed(3));
  }, [newChildPart?.consumption]);

  useEffect(() => {
    setRatioInput(Number(newChildPart?.ratio ?? 0).toFixed(2));
  }, [newChildPart?.ratio]);

  useEffect(() => {
    setPartWeightInput(Number(newChildPart?.part_weight ?? 0).toFixed(3));
  }, [newChildPart?.part_weight]);

  useEffect(() => {
    if (
      !materialCodes ||
      materialCodes.length === 0 ||
      !newChildPart?.material_id
    ) {
      volumeRef.current = 0;
      return;
    }
    const selectedMatCode = materialCodes?.find(
      (code) => code.material_id === newChildPart?.material_id,
    );
    volumeRef.current = selectedMatCode ? selectedMatCode.volume : 0;
  }, [materialCodes, newChildPart?.material_id]);

  useQueryHandler({
    isError: isMaterialCodesError,
  });

  const calculateConsumption = (amount: number, ratio: number) => {
    if (amount && ratio && volumeRef.current) {
      const result = (ratio * volumeRef.current) / (amount * 100);
      return parseDecimal(result.toString(), 3);
    }
    return 0;
  };

  const materialOptions =
    materialCodes?.map((code) => ({
      value: code.material_id,
      label: code.material_code,
    })) || [];

  const selectedMaterialOption = materialOptions.find(
    (opt) => opt.value === newChildPart?.material_id,
  );

  return (
    <>
      <TableCell
        sx={{
          minWidth: 80,
          maxWidth: 80,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
          textAlign: "right",
          fontSize: "0.9rem",
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          sx={{ "& input": { textAlign: "right" } }}
          value={newChildPart?.qty}
          onChange={(e) => {
            const newQty = parseInteger(e.target.value);
            setNewChildPart((prev) => {
              if (!prev) return null;
              return { ...prev, qty: newQty };
            });
            setFlaggedReason((prev) => {
              const newSet = new Set(prev);
              newSet.add("Q'ty");
              return newSet;
            });

          }}
        />
      </TableCell>
      <TableCell
        sx={{
          minWidth: 300,
          maxWidth: 300,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
        }}
      >
        <Tooltip
          title={selectedMaterialOption?.label ?? ""}
          placement="top"
          disableInteractive
          slotProps={{
            tooltip: {
              sx: {
                fontSize: "0.8125rem",
                maxWidth: "none",
                whiteSpace: "nowrap",
              },
            },
          }}
        >
        <Autocomplete
          size="small"
          loading={isMaterialCodesPending}
          fullWidth
          value={selectedMaterialOption || null}
          onChange={(_event, newValue) => {
            const selectedMatCode = materialCodes?.find(
              (code) => code.material_id === newValue?.value,
            );

            setNewChildPart((prev) => {
              if (!prev) return null;
              const newConsumption = calculateConsumption(
                prev.amount || 0,
                prev.ratio || 0,
              );
              return {
                ...prev,
                material_id: newValue ? newValue.value : 0,
                material_price: selectedMatCode
                  ? selectedMatCode.material_price
                  : 0,
                consumption:
                  newConsumption ||
                  (selectedMatCode && selectedMatCode.volume ? selectedMatCode.volume : 0),
                scrap_price: selectedMatCode?.return_scrap_price
                  ? selectedMatCode.return_scrap_price
                  : 0,
              };
            });
            
            setFlaggedReason((prev) => {
              const newSet = new Set(prev);
              newSet.add("Material Code")
              return newSet;
            })
          }}
          options={materialOptions || []}
          slotProps={{
            paper: { sx: { width: "max-content", minWidth: "100%" } },
            listbox: {
              sx: {
                overflowX: "auto",
                "& .MuiAutocomplete-option": { whiteSpace: "nowrap" },
              },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              slotProps={{
                input: {
                  ...params.InputProps,
                  style: { fontSize: "0.875rem" },
                },
              }}
              error={validationErrors.has("material_id")}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: validationErrors.has("material_id")
                    ? "#d32f2f"
                    : undefined,
                  borderWidth: validationErrors.has("material_id") ? 2 : 1,
                },
                "& .MuiOutlinedInput-input": {
                  textOverflow: "ellipsis",
                },
              }}
            />
          )}
        />
        </Tooltip>
      </TableCell>
      <TableCell
        sx={{
          minWidth: 120,
          maxWidth: 120,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
          textAlign: "right",
          fontSize: "0.9rem",
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          sx={{ "& input": { textAlign: "right" } }}
          value={Number(newChildPart?.material_price ?? 0).toFixed(2)}
          disabled
        />
      </TableCell>
      <TableCell
        sx={{
          minWidth: 100,
          maxWidth: 100,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
          textAlign: "right",
          fontSize: "0.9rem",
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          sx={{ "& input": { textAlign: "right" } }}
          value={newChildPart?.amount}
          onChange={(e) => {
            const newAmount = parseInteger(e.target.value);
            setNewChildPart((prev) => {
              if (!prev) return null;
              const newConsumption = calculateConsumption(
                newAmount,
                prev.ratio || 0,
              );
              return {
                ...prev,
                amount: newAmount,
                consumption: newConsumption,
              };
            });
            setFlaggedReason((prev) => {
              const newSet = new Set(prev);
              newSet.add("Amount");
              return newSet;
            });
          }}
          disabled={newChildPart?.is_purchase_part}
        />
      </TableCell>
      <TableCell
        sx={{
          minWidth: 120,
          maxWidth: 120,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
          textAlign: "right",
          fontSize: "0.9rem",
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          sx={{ "& input": { textAlign: "right" } }}
          value={ratioInput}
          onChange={(e) => {
            const val = e.target.value;
            if (isValidDecimalInput(val, 2, false)) {
              setRatioInput(val.trim());
            }
          }}
          onBlur={(e) => {
            const newRatio = parseDecimal(e.target.value, 2);
            setNewChildPart((prev) => {
              if (!prev) return null;
              const newConsumption = calculateConsumption(
                prev.amount || 0,
                newRatio,
              );
              return { ...prev, ratio: newRatio, consumption: newConsumption };
            });
            setFlaggedReason((prev) => {
              const newSet = new Set(prev);
              newSet.add("Ratio");
              return newSet;
            });
          }}
          disabled={newChildPart?.is_purchase_part}
        />
      </TableCell>
      <TableCell
        sx={{
          minWidth: 120,
          maxWidth: 120,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
          textAlign: "right",
          fontSize: "0.9rem",
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          sx={{ "& input": { textAlign: "right" } }}
          value={consumptionInput}
          onChange={(e) => {
            const val = e.target.value;
            if (isValidDecimalInput(val, 3, false)) {
              setConsumptionInput(val.trim());
            }
          }}
          onBlur={(e) => {
            const newConsumption = parseDecimal(e.target.value, 3);
            setNewChildPart((prev) =>
              prev ? { ...prev, consumption: newConsumption } : null,
            );
            setFlaggedReason((prev) => {
              const newSet = new Set(prev);
              newSet.add("Consumption");
              return newSet;
            });
          }}
          disabled={newChildPart?.is_purchase_part}
        />
      </TableCell>
      <TableCell
        sx={{
          minWidth: 120,
          maxWidth: 120,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
          textAlign: "right",
          fontSize: "0.9rem",
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          sx={{ "& input": { textAlign: "right" } }}
          value={partWeightInput}
          onChange={(e) => {
            const val = e.target.value;
            if (isValidDecimalInput(val, 3, false)) {
              setPartWeightInput(val.trim());
            }
          }}
          onBlur={(e) => {
            const newPartWeight = parseDecimal(e.target.value, 3);
            setNewChildPart((prev) =>
              prev ? { ...prev, part_weight: newPartWeight } : null,
            );
            setFlaggedReason((prev) => {
              const newSet = new Set(prev);
              newSet.add("Part Weight");
              return newSet;
            });

          }}
          disabled={newChildPart?.is_purchase_part}
        />
      </TableCell>
      <TableCell
        sx={{
          minWidth: 120,
          maxWidth: 120,
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
          textAlign: "right",
          fontSize: "0.9rem",
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          sx={{ "& input": { textAlign: "right" } }}
          value={((newChildPart?.consumption ?? 0) - (newChildPart?.part_weight ?? 0)).toFixed(3)}
          disabled
        />
      </TableCell>
    </>
  );
};

export default ChildPartInfoDataEdit;
